"use client";

import React from "react";
import {FeefoSummary} from "@/types/dto/destinations-page/destination-page-types";
import {useTranslations, useFormatter} from "next-intl";
import {Stack, Rating, Typography, Box} from "@mui/material";
import {ColletteDivider} from "@collette-travel/collette-common-controls";
import Image from "next/image";

interface FeefoReviewsSummaryProps {
  summary: FeefoSummary;
  isSmFeefoSummary?: boolean;
}

const FeefoReviewsSummary = ({
  summary, 
  isSmFeefoSummary
}: FeefoReviewsSummaryProps) => {
  const t = useTranslations("DESTINATIONS");
  const tSmallPageExplorations = useTranslations("SmallGroupExplorationsPage");
  const formatter = useFormatter();
  
  if (isSmFeefoSummary) {
    return (
      <Stack
        sx={{
          flexDirection: {xs: "column", sm: "row"},
          justifyContent: "center",
          alignItems: "center",
          gap: "1.2rem",
          height: "auto"
        }}
      >
        <Typography data-testid="feefo-summary_description">
          {t.rich("Overall rating", {
            description: summary.ratedText,
            // eslint-disable-next-line react/no-unstable-nested-components
            span: (chunks) => <span style={{fontWeight: 700}}>{chunks}</span>})
          }
        </Typography>
        <Box sx={{ 
          display: "flex", 
          alignItems: "center",
          gap: "0.8rem" 
        }}>
          <Rating
            defaultValue={summary.stars}
            precision={0.1}
            size="small"
            readOnly
            data-testid="feefo-summary_star-rating"
          />
          <Typography 
            data-testid="feefo-summary_rating_count" 
            sx={{ 
              textAlign: "center",
              display: { xs: "none", sm: "block" },
              "@media (min-width: 1200px) and (max-width: 1365px)": {
                display: "none"
              }
            }}
          >
            {tSmallPageExplorations("Rating Out", { rating: summary.stars })}
          </Typography>
          <ColletteDivider
            flexItem
            testId="feefo-summary-divider"
            orientation="vertical"
            sx={{
              display: { xs: "none", sm: "block" },
              borderColor: "divider"
            }}
          />
          <Image src="/images/feefo-logo.png" alt="feefo logo" height={32} width={92} />
        </Box>
      </Stack>
    );
  }
  
  return (
    <Stack
      sx={{
        flexDirection: { xs: "column", lg: "row" },
        justifyContent: "center",
        alignItems: "center",
        gap: "1.2rem",
        height: "auto"
      }}
    >
      <Typography data-testid="feefo-summary_description" sx={{ fontSize:{ xs: "1.4rem", sm: "1.8rem" } }}>
        {t.rich("Overall rating", {
          description: summary.ratedText,
          // eslint-disable-next-line react/no-unstable-nested-components
          span: (chunks) => <span style={{ fontWeight: 700 }}>{chunks}</span>
        })}
      </Typography>
      {/* TODO - replace with common control when added */}
      <Box sx={{ display: { xs: "flex", sm: "contents" }, alignItems: "center", gap: "1.2rem" }}>
        <Rating
          defaultValue={summary.stars}
          precision={0.1}
          size="small"
          readOnly
          data-testid="feefo-summary_star-rating"
        />
        <Typography  data-testid="feefo-summary_rating_count" sx={{ textAlign: "center",fontSize:"1.8rem",fontWeight: 500, display: { xs: "none", sm: "block" } }}>
          {`${t("Rating out of 5", { rating: summary.stars })} ${t("Review count", { count: formatter.number(summary.count) })}`}
        </Typography>
        <ColletteDivider
          flexItem
          testId="feefo-summary-divider"
          orientation="vertical"
          sx={{
            display: { xs: "block", sm: "none", lg: "block" },
            borderColor: "divider"
          }}
        />
        <Box sx={{ position: "relative", width:{ xs: "54px", sm: "92px" }, height: { xs: "18.9px", sm: "32px" } }}>
        <Image
          src="/images/feefo-logo.png"
          alt="feefo logo"
          fill
          data-testid="feefo-logo"
          style={{ objectFit: "cover" }}
        />
        </Box>
      </Box>
    </Stack>
  );
};

export default FeefoReviewsSummary;